import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { registrationConsent } from '../legal/registration';
import finishSignIn from '../utils/finishSignIn';
import { getLocalStorage } from '../utils/func';
import config from '../utils/config';
import { abortable } from '../utils/abortable';
import { readWithRecovery } from '../utils/loadWithRecovery';
import { subscribeAppActivity } from '../utils/appActivity';
import { reviewCopy } from './copy';
import './index.scss';

async function request(path, body, signal) {
  const response = await abortable(fetch(`${(process.env.REACT_APP_API_URL || '').replace(/\/$/, '')}/auth/review/${path}`, {
    method: body ? 'POST' : 'GET', signal, credentials: 'omit', cache: 'no-store',
    ...(body ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}),
  }), signal);
  let data;
  try { data = await abortable(response.json(), signal); }
  catch (error) {
    if (signal?.aborted) throw error;
    throw Object.assign(new Error('REVIEW_UNAVAILABLE'), { status: response.ok ? 502 : response.status });
  }
  if (!response.ok) throw Object.assign(new Error(typeof data?.error === 'string' ? data.error
    : typeof data?.error?.code === 'string' ? data.error.code : 'REVIEW_UNAVAILABLE'), {
    status: response.status, retryAfter: Number(response.headers?.get('Retry-After')) || undefined,
  });
  if (path === 'capabilities' && typeof data?.enabled !== 'boolean') {
    throw Object.assign(new Error('REVIEW_UNAVAILABLE'), { status: 502 });
  }
  return data;
}

export default function ReviewAccess({ consent, disabled, onBusy }) {
  const { i18n } = useTranslation(), navigate = useNavigate(), copy = reviewCopy(i18n.language);
  const [enabled, setEnabled] = useState(false), [open, setOpen] = useState(false);
  const [username, setUsername] = useState(''), [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const mounted = useRef(false), pending = useRef(null);
  useEffect(() => {
    mounted.current = true;
    const abort = new AbortController();
    let failed = false;
    const load = () => {
      failed = false;
      // Only capability reads may retry. A sign-in POST can already have
      // issued a session when its response is lost and is never replayed.
      readWithRecovery(({ signal }) => request('capabilities', null, signal), { signal: abort.signal }).then(data => {
        if (!abort.signal.aborted) setEnabled(data.enabled);
      }).catch(() => { if (!abort.signal.aborted) failed = true; });
    };
    const unsubscribe = subscribeAppActivity(({ active }) => {
      // The recovery budget is finite. After an extended outage, returning
      // online/foreground must restore review access without reopening login.
      if (active && failed) load();
    });
    load();
    return () => { mounted.current = false; unsubscribe(); abort.abort(); pending.current?.abort(); };
  }, []);
  const cancel = () => {
    pending.current?.abort(); pending.current = null; setBusy(false); onBusy(null);
    setPassword(''); setError(''); setOpen(false);
  };
  const submit = async event => {
    event.preventDefault();
    if (!consent || disabled || pending.current) return;
    const abort = new AbortController(); pending.current = abort;
    const startingToken = getLocalStorage(config.token);
    const current = () => mounted.current && pending.current === abort && !abort.signal.aborted
      && getLocalStorage(config.token) === startingToken;
    const registration = registrationConsent();
    setBusy(true); onBusy('review'); setError('');
    const timer = setTimeout(() => abort.abort(), 30000);
    try {
      const data = await abortable(request('login', { username: username.trim(), password, consent: true }, abort.signal), abort.signal);
      if (current()) await abortable(finishSignIn(data, { i18n, navigate, isCurrent: current, registration, signal: abort.signal }), abort.signal);
    } catch (cause) {
      if (mounted.current && pending.current === abort && getLocalStorage(config.token) === startingToken) {
        setError(cause.message === 'REVIEW_LOGIN_FAILED' ? 'invalid'
          : ['REVIEW_TRY_LATER', 'RATE_LIMITED'].includes(cause.message) ? 'limited' : 'unavailable');
      }
    } finally {
      clearTimeout(timer);
      if (mounted.current && pending.current === abort) {
        pending.current = null; setBusy(false); setPassword(''); onBusy(null);
      }
    }
  };
  if (!enabled) return null;
  return <section className="review-access">
    <button type="button" className="review-access-toggle login-support-link" aria-expanded={open} aria-controls="review-access-form"
      disabled={disabled && !busy} onClick={() => open ? cancel() : setOpen(true)}>{copy.title}</button>
    {open && <form id="review-access-form" onSubmit={submit} aria-label={copy.title} aria-busy={busy}>
      <label htmlFor="review-username">{copy.username}</label>
      <input id="review-username" name="username" autoComplete="username" autoCapitalize="none" autoCorrect="off"
        spellCheck={false} maxLength={48} value={username} disabled={busy || disabled} required
        onChange={event => setUsername(event.target.value)} />
      <label htmlFor="review-password">{copy.password}</label>
      <input id="review-password" name="password" type="password" autoComplete="current-password" maxLength={128}
        value={password} disabled={busy || disabled} required onChange={event => setPassword(event.target.value)} />
      {error && <p role="alert" className="error-message">{copy[error]}</p>}
      <div className="review-access-actions">
        <button type="button" onClick={cancel}>{copy.cancel}</button>
        <button type="submit" disabled={!consent || disabled || busy || !username.trim() || !password}
          className={busy ? 'auth-button-busy' : ''}>{copy.submit}</button>
      </div>
    </form>}
  </section>;
}
